import { AddressItemType } from '@@types'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useAddressStore, useFetchMyAddresses } from '@entities/Address'

import { useAuthSafe } from '@features/auth'
import { useModalStore } from '@features/modal'


import { useSlots } from '@widgets/slots'

import Button from '@shared/ui/button'

import { DeleteAddressModal } from '../DeleteAddressModal'
import { AddressItem } from './AddressItem'

import styles from './AddressList.module.scss'

function AddressListSkeleton () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={4}
        width={'100%'}
        height={30}
        style={{ borderRadius: 6, marginBottom: 20 }}
      />
    </SkeletonTheme>
  )
}

interface AddressListProps {
  onEditAddress?: (address?: AddressItemType) => void;
}

export default function AddressList ({ onEditAddress }: AddressListProps) {
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: tm } = useTranslation('modalDelivery')
  const { openModal, closeModal } = useModalStore()
  const { data, loading, refetch } = useFetchMyAddresses({
    showPickupShops: false,
    skip: false,
  })
  const { openSlotsModal } = useSlots()
  const {
    selectedAddress,
    setSelectedAddress,
    setDeliveryType,
  } = useAddressStore()

  const { requireRegistration, isAuthorized } = useAuthSafe()
  const isGuest = requireRegistration || !isAuthorized

  if (loading && !isGuest) return <AddressListSkeleton />

  const serverAddresses = data?.myAddresses?.filter((el) => !el?.shop_id) || []

  const handleSelectAddress = async (address: AddressItemType) => {
    setSelectedAddress(address)
    setDeliveryType('delivery')
    closeModal('delivery')
    openSlotsModal()
  }

  const handleDeleteAddress = (address: AddressItemType) => {
    openModal(
      'deleteAddress',
      <DeleteAddressModal shop={address} onSuccess={refetch} />,
      {
        title: '',
      }
    )
  }

  // Для анонима — показываем только текущий выбранный адрес
  if (isGuest) {
    if (!selectedAddress || selectedAddress.shop_id) {
      // Нет адреса доставки — ничего не показываем
      return null
    }

    return (
      <div className={styles.guestAddress}>
        <div className={styles.guestAddress__current}>
          <AddressItem
            address={selectedAddress}
            selected={true}
            onSelect={() => handleSelectAddress(selectedAddress)}
          />
        </div>
        {onEditAddress && (
          <Button
            type="button"
            theme="secondary"
            size={!isTablet ? 'large' : 'medium'}
            onClick={() => onEditAddress(selectedAddress)}
          >
            {tm('edit-address')}
          </Button>
        )}
      </div>
    )
  }

  // Для авторизованных — список адресов
  if (!serverAddresses.length) return null

  return (
    <>
      <div className={styles.list}>
        {serverAddresses.map((item, index) => (
          <AddressItem
            key={`${item?.id}-${index}`}
            address={item as AddressItemType}
            selected={selectedAddress?.id === item?.id}
            onSelect={() => handleSelectAddress(item as AddressItemType)}
            onEdit={() => onEditAddress?.(item as AddressItemType)}
            onDelete={() => handleDeleteAddress(item as AddressItemType)}
          />
        ))}
      </div>
    </>
  )
}
